import { useMemo, useState } from 'react';
import { navigate } from '../boot/router';
import { canEdit, type AppUser } from '../boot/session';
import { createProject, deleteProject, listProjects } from '../data/repo';
import {
  PROJECT_STATUS_LABEL,
  PROJECT_TYPE_LABEL,
  type ProjectStatus,
  type ProjectType,
} from '../data/types';
import { useAction, useAsync } from '../hooks/useAsync';
import { Modal, ModalCancel } from '../shell/Modal';
import { OneUI } from '../shell/oneUi';
import { EmptyState, ErrorState, LoadingState } from '../shell/State';
import { relTime } from '../shell/bits';

/** 프로젝트 목록 · 생성 · 보관 (작업지시서 P1-04) */
export function ProjectsPage({ user }: { user: AppUser }) {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<ProjectType | 'all'>('all');
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const state = useAsync(() => listProjects(includeArchived), [includeArchived]);

  /**
   * 프로젝트 삭제는 안의 도면을 전부 지운다. 대부분의 경우 **보관**이 맞다 —
   * 그래서 확인창에서 그 사실을 먼저 알린다.
   *
   * 실제 권한 판정은 RLS(0011)가 한다: 만든 사람이면서 안에 남의 도면도
   * 배포본도 없을 때만 지워진다. 여기서는 만든 사람인지만 보고 버튼을 띄우고,
   * 나머지 사유는 서버가 돌려주는 메시지로 알린다.
   */
  async function removeProject(p: { id: string; name: string; drawing_count: number }) {
    const ok = await OneUI.confirm(
      (p.drawing_count > 0
        ? `안에 있는 도면 ${p.drawing_count}개와 그 모든 버전·원본이 함께 사라집니다.\n`
        : '') + '되돌릴 수 없습니다. 나중에 다시 볼 가능성이 있다면 삭제 대신 보관을 쓰세요.',
      { title: `"${p.name}" 프로젝트를 삭제할까요?`, okText: '삭제', danger: true }
    );
    if (!ok) return;

    setDeletingId(p.id);
    setDeleteError(null);
    try {
      await deleteProject(p.id, p.name);
      state.setData((state.data ?? []).filter((x) => x.id !== p.id));
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingId(null);
    }
  }

  const filtered = useMemo(() => {
    const list = state.data ?? [];
    const q = query.trim().toLowerCase();
    return list.filter((p) => {
      if (typeFilter !== 'all' && p.project_type !== typeFilter) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        (p.store_name ?? '').toLowerCase().includes(q) ||
        (p.store_code ?? '').toLowerCase().includes(q)
      );
    });
  }, [state.data, query, typeFilter]);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">프로젝트</h1>
          <p className="page__desc">신규 출점 · 리뉴얼 · 장비 교체</p>
        </div>
        {canEdit(user) && (
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            + 새 프로젝트
          </button>
        )}
      </header>

      <div className="toolbar">
        <input
          className="input input--search"
          type="search"
          placeholder="프로젝트명 · 점포명 · 점포코드 검색"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="프로젝트 검색"
        />
        <select
          className="select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as ProjectType | 'all')}
          aria-label="프로젝트 유형 필터"
        >
          <option value="all">전체 유형</option>
          {(Object.keys(PROJECT_TYPE_LABEL) as ProjectType[]).map((t) => (
            <option key={t} value={t}>
              {PROJECT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        <div className="toolbar__spacer" />
        <label style={{ fontSize: 12, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          보관 포함
        </label>
      </div>

      {state.loading && <LoadingState label="프로젝트를 불러오는 중…" />}

      {!state.loading && state.error && (
        <ErrorState message={state.error} onRetry={state.reload} />
      )}

      {!state.loading && !state.error && filtered.length === 0 && (
        <div className="card">
          <EmptyState
            icon="▣"
            title={query || typeFilter !== 'all' ? '조건에 맞는 프로젝트가 없습니다' : '아직 프로젝트가 없습니다'}
            desc={
              query || typeFilter !== 'all'
                ? '검색어나 필터를 바꿔 보세요.'
                : '첫 프로젝트를 만들어 도면을 등록해 보세요.'
            }
            action={
              canEdit(user) && !query && typeFilter === 'all' ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  style={{ marginTop: 10 }}
                  onClick={() => setCreating(true)}
                >
                  + 새 프로젝트
                </button>
              ) : undefined
            }
          />
        </div>
      )}

      {deleteError && (
        <div className="form-error" role="alert">
          {deleteError}{' '}
          <button type="button" className="section__more" onClick={() => setDeleteError(null)}>
            닫기
          </button>
        </div>
      )}

      {!state.loading && !state.error && filtered.length > 0 && (
        <div className="grid-cards">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="pcard"
              role="button"
              tabIndex={0}
              onClick={() => navigate({ name: 'project', projectId: p.id })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate({ name: 'project', projectId: p.id });
                }
              }}
            >
              <div className="pcard__top">
                <div>
                  <div className="pcard__name">{p.name}</div>
                  {(p.store_name || p.store_code) && (
                    <div className="pcard__store">
                      {p.store_name}
                      {p.store_code ? ` (${p.store_code})` : ''}
                    </div>
                  )}
                </div>
                <div className="pcard__top-right">
                  <span className="badge badge--type">{PROJECT_TYPE_LABEL[p.project_type]}</span>
                  {p.created_by === user.email && (
                    <button
                      type="button"
                      className="btn btn--sm btn--danger"
                      disabled={deletingId !== null}
                      title="내가 만든 프로젝트를 삭제합니다"
                      onClick={(e) => {
                        e.stopPropagation();
                        void removeProject(p);
                      }}
                    >
                      {deletingId === p.id ? '삭제 중…' : '삭제'}
                    </button>
                  )}
                </div>
              </div>

              {p.description && <div className="pcard__desc">{p.description}</div>}

              <div className="pcard__foot">
                <span>도면 {p.drawing_count}</span>
                {p.pending_review_count > 0 && (
                  <span className="badge badge--warn">검토 대기 {p.pending_review_count}</span>
                )}
                {p.status !== 'active' && (
                  <span className="badge badge--status">
                    {PROJECT_STATUS_LABEL[p.status as ProjectStatus]}
                  </span>
                )}
                <span style={{ marginLeft: 'auto' }}>{relTime(p.updated_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {creating && (
        <NewProjectModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            navigate({ name: 'project', projectId: id });
          }}
        />
      )}
    </div>
  );
}

function NewProjectModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState('');
  const [type, setType] = useState<ProjectType>('renewal');
  const [storeName, setStoreName] = useState('');
  const [storeCode, setStoreCode] = useState('');
  const [desc, setDesc] = useState('');
  const { run, busy, error } = useAction();

  const valid = name.trim().length > 0;

  async function submit() {
    if (!valid) return;
    let newId = '';
    const ok = await run(async () => {
      const p = await createProject({
        name,
        project_type: type,
        store_name: storeName || null,
        store_code: storeCode || null,
        description: desc || null,
      });
      newId = p.id;
    });
    if (ok) onCreated(newId);
  }

  return (
    <Modal
      title="새 프로젝트"
      desc="프로젝트를 만들면 자동으로 프로젝트 관리자가 됩니다."
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void submit()}
            disabled={!valid || busy}
          >
            {busy ? '만드는 중…' : '만들기'}
          </button>
        </>
      }
    >
      {error && <div className="form-error">{error}</div>}

      <div className="field">
        <label className="field__label" htmlFor="np-name">
          프로젝트명 *
        </label>
        <input
          id="np-name"
          className="input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="예: 수원점 1층 리뉴얼"
        />
      </div>

      <div className="field">
        <label className="field__label" htmlFor="np-type">
          유형
        </label>
        <select
          id="np-type"
          className="select"
          value={type}
          onChange={(e) => setType(e.target.value as ProjectType)}
        >
          {(Object.keys(PROJECT_TYPE_LABEL) as ProjectType[]).map((t) => (
            <option key={t} value={t}>
              {PROJECT_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field">
          <label className="field__label" htmlFor="np-store">
            점포명
          </label>
          <input
            id="np-store"
            className="input"
            value={storeName}
            onChange={(e) => setStoreName(e.target.value)}
            placeholder="수원점"
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="np-code">
            점포코드
          </label>
          <input
            id="np-code"
            className="input"
            value={storeCode}
            onChange={(e) => setStoreCode(e.target.value)}
            placeholder="S-101"
          />
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="np-desc">
          설명
        </label>
        <input
          id="np-desc"
          className="input"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="신선 코너 확장 및 계산대 재배치"
        />
      </div>
    </Modal>
  );
}
